import React, { useEffect, useRef, useState } from 'react';

export default function SSHTerminal({ server, onClose }) {
  const termRef = useRef(null);
  const wsRef   = useRef(null);
  const xtermRef = useRef(null);
  const [creds, setCreds] = useState({ username: server.username || 'admin', password:'', privateKey:'', port: server.sshPort || 22 });
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [useKey, setUseKey] = useState(false);

  const connect = async (overrideCreds) => {
    setError('');
    setConnecting(true);
    const useCreds = overrideCreds || creds;
    const { Terminal } = await import('xterm');
    const { FitAddon } = await import('xterm-addon-fit');
    await import('xterm/css/xterm.css');

    if (xtermRef.current) xtermRef.current.dispose();
    const term = new Terminal({ cursorBlink:true, fontSize:13, theme:{ background:'#0d1117', foreground:'#c9d1d9', cursor:'#58a6ff' }, fontFamily:'monospace', cols:120, rows:35 });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(termRef.current);
    fit.fit();
    xtermRef.current = term;

    const token = localStorage.getItem('mcp_token');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws/ssh`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        token,
        serverId: server._id,
        host: server.ipAddress,
        port: useCreds.port || 22,
        username: useCreds.username,
        ...(useCreds.privateKey ? { privateKey: useCreds.privateKey } : { password: useCreds.password })
      }));
      setConnected(true); setConnecting(false); term.focus();
    };
    ws.onmessage = e => term.write(e.data);
    ws.onclose = () => { term.write('\r\n\r\n Connection closed.\r\n'); setConnected(false); setConnecting(false); };
    ws.onerror = () => { setError('Connection failed. Check IP and credentials.'); setConnecting(false); };
    term.onData(data => ws.readyState === 1 && ws.send(JSON.stringify({ data })));
    window.addEventListener('resize', () => fit.fit());
  };

  useEffect(() => {
    const fetchAndConnect = async () => {
      try {
        const token = localStorage.getItem('mcp_token');
        const res = await fetch(`/api/servers/${server._id}/ssh-status`, {
          headers: { Authorization: 'Bearer ' + token }
        });
        const data = await res.json();
        if (data.success && data.hasSavedCreds) {
          // Creds are stored server-side; backend will decrypt and use them.
          // We still need SOME value in privateKey/password locally to pick the ws.send branch,
          // but the backend ignores these and uses its own stored creds when serverId matches.
          const autoCreds = { username: data.username || 'root', password: '', privateKey: '__use_saved__', port: data.port || server.sshPort || 22 };
          setCreds(autoCreds);
          setUseKey(true);
          connect(autoCreds);
        }
      } catch(e) {}
    };
    fetchAndConnect();
    return () => { wsRef.current?.close(); xtermRef.current?.dispose(); };
  }, []);

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:9999,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{width:'100%',maxWidth:900,background:'#0d1117',borderRadius:14,border:'1px solid #30363d',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',background:'#161b22',borderBottom:'1px solid #30363d'}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:16}}>🖥️</span>
            <span style={{color:'#58a6ff',fontFamily:'monospace',fontWeight:700}}>{creds.username}@{server.ipAddress}</span>
            <span style={{fontSize:11,color:'#8b949e',fontFamily:'monospace'}}>{server.name}</span>
            {connecting && <span style={{fontSize:10,background:'rgba(245,158,11,0.15)',color:'#f59e0b',padding:'2px 8px',borderRadius:10,border:'1px solid rgba(245,158,11,0.3)'}}>⏳ CONNECTING...</span>}
            {connected && <span style={{fontSize:10,background:'rgba(16,185,129,0.15)',color:'#10b981',padding:'2px 8px',borderRadius:10,border:'1px solid rgba(16,185,129,0.3)'}}>● CONNECTED</span>}
          </div>
          <button onClick={onClose} style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',color:'#ef4444',borderRadius:7,padding:'5px 14px',cursor:'pointer',fontWeight:700}}>✕ Close</button>
        </div>

        {!connected && !connecting && (
          <div style={{padding:20,borderBottom:'1px solid #30363d'}}>
            {error && <div style={{color:'#ef4444',marginBottom:12,fontSize:13}}>❌ {error}</div>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 0.7fr 1fr 1fr',gap:12,marginBottom:12}}>
              <div>
                <label style={{fontSize:11,color:'#8b949e',display:'block',marginBottom:4}}>USERNAME</label>
                <input value={creds.username} onChange={e=>setCreds({...creds,username:e.target.value})}
                  style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:6,padding:'7px 10px',color:'#c9d1d9',fontSize:13,boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:11,color:'#8b949e',display:'block',marginBottom:4}}>PORT</label>
                <input type="number" value={creds.port} onChange={e=>setCreds({...creds,port:e.target.value})}
                  style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:6,padding:'7px 10px',color:'#c9d1d9',fontSize:13,boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:11,color:'#8b949e',display:'block',marginBottom:4}}>AUTH TYPE</label>
                <select value={useKey?'key':'password'} onChange={e=>setUseKey(e.target.value==='key')}
                  style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:6,padding:'7px 10px',color:'#c9d1d9',fontSize:13}}>
                  <option value="password">Password</option>
                  <option value="key">Private Key</option>
                </select>
              </div>
              <div>
                <label style={{fontSize:11,color:'#8b949e',display:'block',marginBottom:4}}>{useKey?'PRIVATE KEY':'PASSWORD'}</label>
                {useKey
                  ? <textarea value={creds.privateKey} onChange={e=>setCreds({...creds,privateKey:e.target.value})} rows={1}
                      style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:6,padding:'7px 10px',color:'#c9d1d9',fontSize:11,resize:'none',boxSizing:'border-box'}}/>
                  : <input type="password" value={creds.password} onChange={e=>setCreds({...creds,password:e.target.value})}
                      onKeyDown={e=>e.key==='Enter'&&connect()}
                      style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:6,padding:'7px 10px',color:'#c9d1d9',fontSize:13,boxSizing:'border-box'}}/>}
              </div>
            </div>
            <button onClick={()=>connect()} style={{background:'#238636',border:'none',color:'#fff',borderRadius:7,padding:'8px 24px',cursor:'pointer',fontWeight:700,fontSize:13}}>
              🔐 Connect via SSH
            </button>
          </div>
        )}
        <div ref={termRef} style={{padding:10,minHeight:400}}/>
      </div>
    </div>
  );
}
